import { useState, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Modal from '@/components/modal/Modal.tsx';
import ModalHeader from '@/components/modal/ModalHeader.tsx';
import ModalContent from '@/components/modal/ModalContent.tsx';
import ModalFooter from '@/components/modal/ModalFooter.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import PrimitiveCheckbox from '@/components/form/_primitives/PrimitiveCheckbox.tsx';
import { InfoCardList, InfoCardRow } from '@/components/ui/InfoCard.tsx';
import useGetPlannedTransaction from '@/features/dashboard/plannedTransactions/hooks/useGetPlannedTransaction.ts';
import useCreateFinancialTransaction from '@/features/dashboard/financialTransactions/hooks/useCreateFinancialTransaction.ts';
import {
  financialTransactionSchema,
  FinancialTransactionFormData,
  FinancialTransactionInputData,
} from '@/features/dashboard/financialTransactions/schemas/financialTransactionSchema.ts';
import FinancialTransactionForm from '@/features/dashboard/financialTransactions/components/FinancialTransactionForm.tsx';
import { scrollToAllocationsError } from '@/features/dashboard/financialTransactions/components/allocationsError.ts';
import MarkAsPaidModalSkeleton from '@/features/dashboard/plannedTransactions/components/MarkAsPaidModalSkeleton.tsx';
import { plannedTransactionDetailDtoMapper } from '@/features/dashboard/plannedTransactions/types/PlannedTransactionDetailDto.mapper.ts';
import { Currency } from '@/features/dashboard/paymentConfig/types/Currency.ts';
import { formatPrice } from '@/utils/formatters.ts';
import timeUtils from '@/utils/timeUtils.ts';

interface MarkAsPaidModalProps {
  plannedTransactionId: string;
  clientId: string;
  onClose: () => void;
}

const MarkAsPaidModal = ({
  plannedTransactionId,
  clientId,
  onClose,
}: MarkAsPaidModalProps) => {
  const [showForm, setShowForm] = useState(false);
  const { data: transaction, isLoading } =
    useGetPlannedTransaction(plannedTransactionId);
  const createMutation = useCreateFinancialTransaction();

  const methods = useForm<FinancialTransactionInputData>({
    resolver: zodResolver(financialTransactionSchema),
  });

  const { handleSubmit, watch, reset } = methods;

  useEffect(() => {
    if (transaction) {
      reset(
        plannedTransactionDetailDtoMapper.fromDtoToForm(transaction, clientId),
      );
    }
  }, [transaction, clientId, reset]);

  const watchedAmount = watch('amount');
  const watchedCurrency = watch('currency');
  const watchedDate = watch('transactionDate');

  const onSubmit = (data: FinancialTransactionFormData) => {
    createMutation.mutate(data, { onSuccess: () => onClose() });
  };

  const formatAmount = () => {
    if (watchedAmount === undefined) return '';
    return formatPrice(Number(watchedAmount), watchedCurrency ?? Currency.PLN);
  };

  const formatDate = () => {
    if (!watchedDate) return '';
    return timeUtils.toDate(watchedDate);
  };

  return (
    <Modal onClose={onClose} isDirty={methods.formState.isDirty}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit, scrollToAllocationsError)}>
          <ModalHeader title="Oznacz jako opłacone" onClose={onClose} />
          <ModalContent>
            {isLoading ? (
              <MarkAsPaidModalSkeleton />
            ) : (
              <>
                <div className="space-y-4">
                  <p className="text-sm text-neutral-700">
                    Czy na pewno chcesz oznaczyć tę wpłatę jako opłaconą?
                  </p>
                  <InfoCardList>
                    <InfoCardRow layout="columns" label="Kwota">
                      {formatAmount()}
                    </InfoCardRow>
                    <InfoCardRow layout="columns" label="Data transakcji">
                      {formatDate()}
                    </InfoCardRow>
                  </InfoCardList>
                </div>

                <div className="flex items-center gap-2 py-2">
                  <PrimitiveCheckbox
                    id="showForm"
                    checked={showForm}
                    onChange={(e) => setShowForm(e.target.checked)}
                  />
                  <label
                    htmlFor="showForm"
                    className="cursor-pointer text-sm font-medium"
                  >
                    Dostosuj dane wpłaty
                  </label>
                </div>

                {/* `edit`, bo rata jest już rozstrzygnięta: kierunek i zapisujący
                    są dane rezerwacją, operator poprawia tylko kwotę i datę. */}
                {showForm && (
                  <FinancialTransactionForm
                    mode="edit"
                    isSavedTransaction={false}
                  />
                )}
              </>
            )}
          </ModalContent>
          <ModalFooter>
            <ActionButton
              tone="ghost"
              size="sm"
              type="button"
              onClick={onClose}
            >
              Anuluj
            </ActionButton>
            <ActionButton
              tone="accent"
              size="sm"
              type="submit"
              isLoading={createMutation.isPending}
              disabled={isLoading}
            >
              Oznacz jako opłacone
            </ActionButton>
          </ModalFooter>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default MarkAsPaidModal;
